import { configVars } from 'src/helpers';
import { IOwnerPropertiesResponse } from './hooks/useOwnerProperties';
import {
  IAvailablePropertiesResponse,
  IOwnerRequest,
  IOwnerResponse,
  IOwnerUpdateRequest
} from './ts/interfaces/Owner';

export interface AddressData {
  address: string;
  city: string;
  state: string;
  zip: string;
}

const api = {
  getOwner: async (id: string) => {
    const response = await fetch(`${configVars.server_url}/v2/owners/${id}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch owner');
    }
    return response.json();
  },

  createOwner: async (data: IOwnerRequest) => {
    const response = await fetch(`${configVars.server_url}/v2/owners`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error('Failed to create owner');
    }
    return response.json();
  },

  updateOwner: async (id: string, data: IOwnerUpdateRequest) => {
    const response = await fetch(`${configVars.server_url}/v2/owners/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error('Failed to update owner');
    }
    return response.json();
  },

  deleteOwner: async (id: string) => {
    const response = await fetch(`${configVars.server_url}/v2/owners/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      throw new Error('Failed to delete owner');
    }
    return response.json();
  },

  checkOwnerNameAvailability: async (name: string) => {
    const response = await fetch(
      `${configVars.server_url}/v2/owners/check-name?name=${encodeURIComponent(name)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Failed to check name availability');
    }
    return response.json();
  },

  checkOwnerAddressAvailability: async ({ address, city, state, zip }: AddressData) => {
    const response = await fetch(
      `${configVars.server_url}/v2/owners/check-address?address=${encodeURIComponent(
        address
      )}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(
        state
      )}&zip=${encodeURIComponent(zip)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Failed to check address availability');
    }
    return response.json();
  },

  checkOwnerSalesforceIdAvailability: async (salesforceId: string) => {
    const response = await fetch(
      `${configVars.server_url}/v2/owners/check-salesforce-id?salesforceId=${encodeURIComponent(
        salesforceId
      )}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Failed to check Salesforce ID availability');
    }
    return response.json();
  },

  getAllOwners: async (params: {
    page?: number;
    rowsPerPage?: number;
    q?: string;
    sort?: 'desc' | 'asc';
    filter?: string;
  }): Promise<IOwnerResponse> => {
    // Check to see if each param exists, if not don't include it in the URL
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.rowsPerPage) queryParams.append('rowsPerPage', params.rowsPerPage.toString());
    if (params.q) queryParams.append('q', params.q);
    if (params.sort) queryParams.append('sort', `inserted_at:${params.sort}`);
    if (params.filter) queryParams.append('filter', params.filter);

    const response = await fetch(
      `${configVars.server_url}/v2/owners${queryParams.toString() ? `?${queryParams}` : ''}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch owners');
    }
    return response.json();
  },

  searchOwners: async (searchParams: {
    q?: string;
    page?: number;
    rowsPerPage?: number;
    sort?: string;
    filter?: string;
  }): Promise<{ owners: IOwnerResponse[]; total: number }> => {
    const response = await fetch(`${configVars.server_url}/v2/owners/_search`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(searchParams)
    });
    if (!response.ok) {
      throw new Error('Failed to search owners');
    }
    return response.json();
  },

  updateOwnerStatus: async (id: string, status: 'ACTIVE' | 'DISABLED') => {
    const response = await fetch(`${configVars.server_url}/v2/owners/${id}/toggle-status`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    });
    if (!response.ok) {
      throw new Error('Failed to update owner status');
    }
    return response.json();
  },

  updatePropertyOwner: async (propertyId: string, ownerId: string | null) => {
    const response = await fetch(
      `${configVars.server_url}/v2/properties/${propertyId}/update-owner`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ownerId })
      }
    );
    if (!response.ok) {
      throw new Error('Failed to update property owner');
    }
    return response.json();
  },

  getPropertyOwner: async (propertyId: string) => {
    const response = await fetch(`${configVars.server_url}/v2/properties/${propertyId}/owner`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch property owner');
    }
    return response.json();
  },

  /**
   * Returns properties linked to a specific owner
   * @param ownerId
   * @param params  pagination and filter params
   */
  getOwnerProperties: async (
    ownerId: string,
    params: {
      page?: number;
      rowsPerPage?: number;
      currentOnly?: boolean; // indicates whether to fetch only active properties for an owner
    } = {}
  ): Promise<IOwnerPropertiesResponse> => {
    const { page = 0, rowsPerPage = 20, currentOnly = true } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('rowsPerPage', rowsPerPage.toString());
    queryParams.append('currentOnly', currentOnly.toString());

    const response = await fetch(
      `${configVars.server_url}/v2/owners/${ownerId}/properties?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch owner properties');
    }
    return response.json();
  },

  getAllOwnerProperties: async (ownerId: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', '0');
    queryParams.append('rowsPerPage', '9999999');
    queryParams.append('currentOnly', 'true');
    const response = await fetch(
      `${configVars.server_url}/v2/owners/${ownerId}/properties?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch all owner properties');
    }
    return response.json();
  },

  /**
   * Syncs owner properties - replaces all current property links with the provided list
   * This is ideal for multi-select UIs where the sent array represents the complete desired state
   * @param ownerId - The owner to sync properties for
   * @param propertyIds - Complete list of property IDs that should be linked to this owner
   * @returns Promise that resolves when sync is complete
   */
  syncOwnerProperties: async (ownerId: string, propertyIds: string[]) => {
    const response = await fetch(`${configVars.server_url}/v2/owners/${ownerId}/properties/sync`, {
      method: 'PUT', // PUT semantics: replace existing resource
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ propertyIds })
    });

    if (!response?.ok) {
      if (response?.status === 409) {
        const responseJson = await response.json();
        const conflictMessage = responseJson?.message;
        const errorDetails = responseJson?.conflicts;
        console.error('Conflict details while syncing owner properties:', errorDetails);
        throw new Error(conflictMessage || 'Conflict occurred while syncing owner properties');
      }
      throw new Error('Failed to sync owner properties');
    }
    return response.json();
  },

  /**
   * Gets properties available to be linked to a specific owner (excludes already owned properties by other owners)
   * Uses POST method to support query parameters in request body and allow for search functionality
   * @param ownerId - The owner to get available properties for
   * @param params - Pagination, search and filter parameters
   * @returns Promise that resolves with available properties and pagination info
   */
  getAvailableProperties: async (
    ownerId: string,
    params: {
      page?: number;
      rowsPerPage?: number;
      q?: string; // search query
    } = {}
  ): Promise<IAvailablePropertiesResponse> => {
    const { page = 0, rowsPerPage = 20, q } = params;
    const base_url = `${configVars.server_url}/v2/owners/${ownerId}/available-properties`;

    // Build query params for URL (for search functionality)
    const queryParams = new URLSearchParams();
    if (q) queryParams.append('q', q);

    // Build request body for pagination
    const requestBody = {
      page: page,
      rowsPerPage: rowsPerPage
    };

    const url = `${base_url}${queryParams.toString() ? `?${queryParams}` : ''}`;

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch available properties');
    }
    return response.json();
  }
};

export default api;
